import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { eur, dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Shuffle, History } from "lucide-react";

const empty = {
  code: "", description: "", discount_type: "percent", discount_value: 10, valid_from: "", valid_to: "",
  max_uses: "", once_per_partner: false, all_packages: true, package_ids: [], all_partners: true, assigned_partners: [], active: true,
};

const randomCode = () => "ATH-" + Math.random().toString(36).slice(2, 8).toUpperCase();
const today = () => new Date().toISOString().slice(0, 10);

export const promoStatus = (p) => {
  const t = today();
  if (!p.active) return { label: "Disattivo", cls: "bg-slate-100 text-slate-600" };
  if (p.valid_to && t > p.valid_to) return { label: "Scaduto", cls: "bg-red-100 text-red-700" };
  if (p.valid_from && t < p.valid_from) return { label: "Programmato", cls: "bg-sky-100 text-sky-800" };
  if (p.max_uses && p.uses_count >= p.max_uses) return { label: "Esaurito", cls: "bg-orange-100 text-orange-800" };
  return { label: "Attivo", cls: "bg-emerald-100 text-emerald-800" };
};

const discountLabel = (p) => (p.discount_type === "percent" ? `-${p.discount_value}%` : `-${eur(p.discount_value)}`);

function MultiCheck({ items, selected, onToggle, labelOf, testPrefix }) {
  return (
    <div className="border rounded-lg p-2 max-h-44 overflow-y-auto">
      {items.map((it) => (
        <label key={it.id} className="flex items-center gap-2 py-1 px-2 rounded cursor-pointer hover:bg-slate-50">
          <Checkbox checked={selected.includes(it.id)} onCheckedChange={() => onToggle(it.id)} data-testid={`${testPrefix}-${it.id}`} />
          <span className="text-sm">{labelOf(it)}</span>
        </label>
      ))}
      {items.length === 0 && <div className="text-sm text-slate-500 p-2">Nessun elemento disponibile.</div>}
    </div>
  );
}

function UsesDialog({ promo, onClose }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { if (promo) api.get(`/promo-codes/${promo.id}/uses`).then((r) => setRows(r.data)); }, [promo]);
  return (
    <Dialog open={!!promo} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="promo-uses-dialog">
        <DialogHeader><DialogTitle>Utilizzi · {promo?.code}</DialogTitle></DialogHeader>
        <div className="max-h-80 overflow-y-auto border rounded-lg divide-y">
          {rows.length === 0 && <div className="p-4 text-sm text-slate-500">Nessun utilizzo confermato.</div>}
          {rows.map((u) => (
            <div key={u.id} className="p-3 text-sm flex justify-between gap-2">
              <div><div className="font-semibold">{u.partner_name || u.partner_id}</div><div className="text-xs text-slate-500">{u.order_number} · {dateIt(u.used_at)}</div></div>
              <div className="font-semibold text-emerald-700">-{eur(u.discount_amount)}</div>
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPromoCodes() {
  const [codes, setCodes] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [usesOf, setUsesOf] = useState(null);

  const load = useCallback(() => api.get("/promo-codes").then((r) => setCodes(r.data)), []);
  useEffect(() => {
    load();
    api.get("/recharge-packages").then((r) => setPkgs(r.data));
    api.get("/partners").then((r) => setPartners(r.data)).catch(() => setPartners([]));
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIn = (k, id) => setForm((f) => {
    const s = new Set(f[k] || []); if (s.has(id)) s.delete(id); else s.add(id);
    return { ...f, [k]: [...s] };
  });

  const payload = (f) => ({
    ...f,
    discount_value: Number(f.discount_value || 0),
    max_uses: f.max_uses === "" || f.max_uses === null ? null : parseInt(f.max_uses, 10),
    valid_from: f.valid_from || null, valid_to: f.valid_to || null,
  });

  const save = async () => {
    if (!form.code || form.code.trim().length < 3) return toast.error("Codice obbligatorio (min 3 caratteri)");
    try {
      if (editing) await api.put(`/promo-codes/${editing}`, payload(form));
      else await api.post("/promo-codes", payload(form));
      toast.success(editing ? "Codice aggiornato" : "Codice creato");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg : d || "Errore salvataggio");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare il codice promo?")) return;
    await api.delete(`/promo-codes/${id}`); toast.success("Codice eliminato"); load();
  };
  const edit = (p) => {
    setEditing(p.id);
    setForm({ ...empty, ...p, valid_from: p.valid_from || "", valid_to: p.valid_to || "", max_uses: p.max_uses ?? "" });
    setOpen(true);
  };
  const toggleActive = async (p) => { await api.put(`/promo-codes/${p.id}`, payload({ ...p, active: !p.active })); load(); };

  return (
    <div className="space-y-6" data-testid="admin-promo-codes">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Codici Promo Ricariche</h1>
          <p className="text-slate-500 mt-1">Crea codici sconto temporanei applicabili all'acquisto dei pacchetti di ricarica.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ ...empty, code: randomCode() }); setOpen(true); }} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="new-promo-button">
          <Plus className="w-4 h-4 mr-1" /> Nuovo codice
        </Button>
      </div>

      <Card className="border-slate-200"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Codice</TableHead><TableHead>Sconto</TableHead><TableHead>Validità</TableHead>
            <TableHead>Utilizzi</TableHead><TableHead>Pacchetti</TableHead><TableHead>Partner</TableHead>
            <TableHead>Stato</TableHead><TableHead>Attivo</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {codes.map((p) => {
              const st = promoStatus(p);
              return (
                <TableRow key={p.id} data-testid={`promo-row-${p.code}`}>
                  <TableCell>
                    <div className="font-mono font-bold text-slate-900">{p.code}</div>
                    {p.description && <div className="text-xs text-slate-500">{p.description}</div>}
                  </TableCell>
                  <TableCell className="font-semibold text-amber-700">{discountLabel(p)}</TableCell>
                  <TableCell className="text-xs">{p.valid_from ? dateIt(p.valid_from) : "—"} → {p.valid_to ? dateIt(p.valid_to) : "∞"}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => setUsesOf(p)} data-testid={`promo-uses-${p.code}`}>
                      <History className="w-3.5 h-3.5 mr-1" /> {p.uses_count || 0}{p.max_uses ? ` / ${p.max_uses}` : ""}
                    </Button>
                    {p.once_per_partner && <div className="text-[10px] text-slate-500 px-3">1 per partner</div>}
                  </TableCell>
                  <TableCell className="text-xs">{p.all_packages ? "Tutti" : `${p.package_ids.length} selezionati`}</TableCell>
                  <TableCell className="text-xs">{p.all_partners ? "Tutti" : `${p.assigned_partners.length} selezionati`}</TableCell>
                  <TableCell><Badge className={`${st.cls} hover:${st.cls}`} data-testid={`promo-status-${p.code}`}>{st.label}</Badge></TableCell>
                  <TableCell><Switch checked={!!p.active} onCheckedChange={() => toggleActive(p)} data-testid={`toggle-promo-${p.code}`} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" onClick={() => edit(p)} data-testid={`edit-promo-${p.code}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(p.id)} data-testid={`delete-promo-${p.code}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {codes.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-10">Nessun codice promo creato.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="promo-dialog">
          <DialogHeader><DialogTitle>{editing ? "Modifica codice promo" : "Nuovo codice promo"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Codice *</Label>
              <div className="flex gap-2">
                <Input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} className="font-mono" data-testid="promo-code-input" />
                <Button type="button" variant="outline" onClick={() => set("code", randomCode())} data-testid="promo-generate-code"><Shuffle className="w-4 h-4 mr-1" /> Genera</Button>
              </div>
            </div>
            <div className="sm:col-span-2"><Label>Descrizione</Label><Input value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="promo-description" /></div>
            <div><Label>Tipo sconto</Label>
              <Select value={form.discount_type} onValueChange={(v) => set("discount_type", v)}>
                <SelectTrigger data-testid="promo-discount-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentuale (%)</SelectItem>
                  <SelectItem value="fixed">Importo fisso (€)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Valore sconto {form.discount_type === "percent" ? "%" : "€"} *</Label>
              <Input type="number" step="0.01" min="0" value={form.discount_value} onChange={(e) => set("discount_value", e.target.value)} data-testid="promo-discount-value" />
            </div>
            <div><Label>Valido dal</Label><Input type="date" value={form.valid_from} onChange={(e) => set("valid_from", e.target.value)} data-testid="promo-valid-from" /></div>
            <div><Label>Valido fino al</Label><Input type="date" value={form.valid_to} onChange={(e) => set("valid_to", e.target.value)} data-testid="promo-valid-to" /></div>
            <div><Label>Limite totale utilizzi</Label><Input type="number" min="1" placeholder="Illimitato" value={form.max_uses} onChange={(e) => set("max_uses", e.target.value)} data-testid="promo-max-uses" /></div>
            <div className="flex items-end gap-3 pb-2">
              <Switch checked={!!form.once_per_partner} onCheckedChange={(v) => set("once_per_partner", v)} data-testid="promo-once-per-partner" />
              <Label>Max 1 utilizzo per partner</Label>
            </div>
          </div>
          <p className="text-xs text-slate-500">Lo sconto si applica all'imponibile del pacchetto (IVA ricalcolata). Il credito accreditato resta quello pieno. L'utilizzo viene conteggiato solo a pagamento confermato.</p>
          <div className="flex items-center gap-3">
            <Switch checked={!!form.all_packages} onCheckedChange={(v) => set("all_packages", v)} data-testid="promo-all-packages" />
            <Label>Valido per tutti i pacchetti</Label>
          </div>
          {!form.all_packages && <MultiCheck items={pkgs} selected={form.package_ids} onToggle={(id) => toggleIn("package_ids", id)} labelOf={(p) => `${p.name} · ${eur(p.total)}`} testPrefix="promo-pkg" />}
          <div className="flex items-center gap-3">
            <Switch checked={!!form.all_partners} onCheckedChange={(v) => set("all_partners", v)} data-testid="promo-all-partners" />
            <Label>Valido per tutti i partner</Label>
          </div>
          {!form.all_partners && <MultiCheck items={partners} selected={form.assigned_partners} onToggle={(id) => toggleIn("assigned_partners", id)} labelOf={(p) => `${p.company_name} · ${p.email}`} testPrefix="promo-partner" />}
          <div className="flex items-center gap-3">
            <Switch checked={!!form.active} onCheckedChange={(v) => set("active", v)} data-testid="promo-active" />
            <Label>Codice attivo</Label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="save-promo">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UsesDialog promo={usesOf} onClose={() => setUsesOf(null)} />
    </div>
  );
}
